import { useState } from "react";
import { toast } from "sonner";
import { ExternalLink, Pencil, Trash2, Users, CheckCircle2, Lock, PackageCheck, Archive } from "lucide-react";
import { api, formatPrice } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";

const priorityColor = {
  cok_istiyorum: "bg-primary/10 text-primary",
  istiyorum: "bg-secondary text-foreground",
  olursa_guzel: "bg-muted text-muted-foreground",
};

export const ItemCard = ({ item, isOwner, onChanged, onEdit, eventPassed = false, archived = false }) => {
  const { user } = useAuth();
  const { t } = useLang();
  const [contribOpen, setContribOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);

  const collected = item.collected_amount || 0;
  const target = item.target_amount || item.price || 0;
  const pct = target ? Math.min(100, (collected / target) * 100) : 0;

  const reserve = async (status) => {
    if (!user) { toast.error(t("login_required_res")); return; }
    setBusy(true);
    try {
      await api.post(`/items/${item.item_id}/reserve`, { status });
      toast.success(status === "purchased" ? t("marked_purchased") : t("reserved_ok"), {
        description: t("surprise_safe"),
      });
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("error_generic"));
    } finally { setBusy(false); }
  };

  const unreserve = async () => {
    setBusy(true);
    try {
      await api.delete(`/items/${item.item_id}/reserve`);
      toast.success(t("unreserved"));
      onChanged?.();
    } catch (err) {
      console.error("unreserve failed", err);
      toast.error(t("error_generic"));
    } finally { setBusy(false); }
  };

  const contribute = async () => {
    const val = parseFloat(amount);
    if (!val || val <= 0) { toast.error(t("valid_amount")); return; }
    setBusy(true);
    try {
      await api.post(`/items/${item.item_id}/contribute`, { amount: val });
      toast.success(t("contributed", { p: formatPrice(val) }));
      setContribOpen(false); setAmount("");
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("error_generic"));
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm(t("del_item_confirm"))) return;
    try {
      await api.delete(`/items/${item.item_id}`);
      toast.success(t("item_deleted"));
      onChanged?.();
    } catch (err) {
      console.error("delete item failed", err);
      toast.error(t("error_generic"));
    }
  };

  const toggleReceived = async () => {
    setBusy(true);
    try {
      await api.post(`/items/${item.item_id}/received`, { received: !item.received });
      toast.success(!item.received ? t("arrived_marked") : t("arrived_unmarked"));
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("error_generic"));
    } finally { setBusy(false); }
  };

  const openStore = () => {
    api.post(`/items/${item.item_id}/click`).catch((err) => console.error("click track failed", err));
    window.open(item.product_url, "_blank", "noopener,noreferrer");
  };

  const reserved = !isOwner && item.status !== "available";

  const storeIconButton = (testId) => item.product_url && (
    <Button variant="outline" size="icon" className="rounded-full" onClick={openStore} aria-label={t("aria_open_store")} data-testid={testId}>
      <ExternalLink size={14} />
    </Button>
  );

  const renderOwnerActions = () => (
    <>
      {eventPassed && (
        <Button size="sm" variant={item.received ? "default" : "outline"} className="rounded-full flex-1"
          disabled={busy} onClick={toggleReceived} data-testid={`item-received-btn-${item.item_id}`}>
          <PackageCheck size={14} className="mr-1" /> {item.received ? t("arrived_check") : t("mark_arrived")}
        </Button>
      )}
      {item.product_url && !eventPassed && (
        <Button variant="outline" size="sm" className="rounded-full flex-1" onClick={openStore} data-testid={`item-store-btn-${item.item_id}`}>
          <ExternalLink size={14} className="mr-1" /> {t("store_btn")}
        </Button>
      )}
      <Button variant="outline" size="icon" className="rounded-full" onClick={() => onEdit?.(item)} aria-label={t("aria_edit")} data-testid={`item-edit-btn-${item.item_id}`}>
        <Pencil size={14} />
      </Button>
      <Button variant="outline" size="icon" className="rounded-full text-destructive" onClick={remove} aria-label={t("aria_delete")} data-testid={`item-delete-btn-${item.item_id}`}>
        <Trash2 size={14} />
      </Button>
    </>
  );

  const renderGroupGiftActions = () => (
    <>
      <Dialog open={contribOpen} onOpenChange={setContribOpen}>
        <DialogTrigger asChild>
          <Button size="sm" className="rounded-full flex-1 active:scale-95 transition-transform" disabled={busy} data-testid={`item-contribute-btn-${item.item_id}`}>
            <Users size={14} className="mr-1" /> {t("contribute")}
          </Button>
        </DialogTrigger>
        <DialogContent className="rounded-2xl max-w-sm">
          <DialogHeader><DialogTitle className="font-heading">{t("contrib_title")}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">{t("contrib_desc", { name: item.name })}</p>
          <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="500" className="rounded-xl" data-testid="contribute-amount-input" />
          <DialogFooter>
            <Button onClick={contribute} disabled={busy} className="rounded-full w-full" data-testid="contribute-submit-btn">{t("contribute")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {storeIconButton(`item-groupbuy-btn-${item.item_id}`)}
    </>
  );

  const renderVisitorActions = () => {
    if (archived) {
      return (
        <Button size="sm" variant="secondary" className="rounded-full flex-1" disabled>
          <Archive size={14} className="mr-1" /> {t("list_archived_btn")}
        </Button>
      );
    }
    if (item.is_group_gift) return renderGroupGiftActions();
    if (item.status === "available") {
      return (
        <>
          <Button size="sm" className="rounded-full flex-1 active:scale-95 transition-transform" disabled={busy}
            onClick={() => reserve("reserved")} data-testid={`item-reserve-btn-${item.item_id}`}>
            <CheckCircle2 size={14} className="mr-1" /> {t("reserve_btn")}
          </Button>
          {storeIconButton(`item-buy-btn-${item.item_id}`)}
        </>
      );
    }
    if (item.reserved_by_me) {
      return (
        <>
          <Button variant="outline" size="sm" className="rounded-full flex-1" disabled={busy} onClick={unreserve} data-testid={`item-unreserve-btn-${item.item_id}`}>
            {t("unreserve")}
          </Button>
          {item.status === "reserved" && (
            <Button size="sm" className="rounded-full" disabled={busy} onClick={() => reserve("purchased")} data-testid={`item-purchased-btn-${item.item_id}`}>
              {t("i_bought")}
            </Button>
          )}
        </>
      );
    }
    return (
      <Button size="sm" variant="secondary" className="rounded-full flex-1" disabled>
        {t("taken_by_other")}
      </Button>
    );
  };

  return (
    <div className="bg-card rounded-2xl border border-border/50 card-lift" data-testid={`item-card-${item.item_id}`}>
      <div className="p-4">
        <div className="flex items-center gap-1.5 flex-wrap mb-2.5">
          <Badge className={`rounded-full border-0 ${priorityColor[item.priority] || ""}`}>
            {t(`prio_${item.priority}`)}
          </Badge>
          {reserved && (
            <Badge className="bg-foreground/90 text-white rounded-full border-0" data-testid={`item-reserved-badge-${item.item_id}`}>
              <Lock size={11} className="mr-1" /> {item.status === "purchased" ? t("purchased_badge") : t("reserved_badge")}
            </Badge>
          )}
          {isOwner && item.received && (
            <Badge className="rounded-full bg-primary text-white border-0" data-testid={`item-received-badge-${item.item_id}`}>
              <PackageCheck size={11} className="mr-1" /> {t("arrived_badge")}
            </Badge>
          )}
          {item.is_group_gift && isOwner && (
            <Badge variant="outline" className="rounded-full"><Users size={11} className="mr-1" /> {t("group_gift")}</Badge>
          )}
        </div>
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="font-heading font-semibold truncate" data-testid={`item-name-${item.item_id}`}>{item.name}</h3>
            {item.store && <p className="text-xs text-muted-foreground">{item.store}</p>}
          </div>
          {item.price != null && <p className="font-heading font-bold text-primary whitespace-nowrap">{formatPrice(item.price, item.currency)}</p>}
        </div>

        {item.is_group_gift && !isOwner && (
          <div className="mt-3">
            <div className="flex justify-between text-xs mb-1.5">
              <span className="text-muted-foreground">{t("collected")}: <b className="text-foreground">{formatPrice(collected)}</b></span>
              <span className="text-muted-foreground">{t("remaining")}: <b className="text-foreground">{formatPrice(Math.max(0, target - collected))}</b></span>
            </div>
            <Progress value={pct} className="h-3 rounded-full" />
          </div>
        )}

        <div className="flex gap-2 mt-4">
          {isOwner ? renderOwnerActions() : renderVisitorActions()}
        </div>
      </div>
    </div>
  );
};
